import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Container, Button, Alert, Card, Spinner } from 'react-bootstrap';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Email verification landing page (#299).
 *
 * Verification is deliberately NOT enforced, so this page never gates anything —
 * arriving here is how a user confirms their address, and never arriving keeps
 * their account working exactly as before.
 *
 * It verifies on mount rather than behind a button. The user already expressed
 * intent by clicking the link in the email; asking them to click again is a
 * second chance to abandon the flow for no benefit.
 */
function VerifyEmail() {
  const [searchParams] = useSearchParams();
  // 'verifying' | 'success' | 'expired' | 'missing' | 'error'
  const [state, setState] = useState('verifying');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('');

  // React 18 StrictMode mounts effects twice in development. The token is
  // single-use, so a second call would consume it and report "expired" on a link
  // that had just succeeded.
  const attempted = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setState('missing');
      return;
    }
    if (attempted.current) return;
    attempted.current = true;

    const verify = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        if (response.ok) {
          setState('success');
          return;
        }

        const data = await response.json().catch(() => null);
        const detail = data?.detail || '';

        // The backend returns 400 for unknown, already-used and expired tokens
        // alike. Treated as one case on purpose: from the user's side they are the
        // same situation, and the remedy — request a new link — is identical.
        setState(response.status === 400 ? 'expired' : 'error');
        setMessage(detail);
      } catch (err) {
        setState('error');
        setMessage('Could not reach the server. Please try again.');
      }
    };

    verify();
  }, [searchParams]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendState('sending');
    try {
      const response = await fetch(`${API_BASE_URL}/users/verify-email/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail })
      });
      // The endpoint always reports success so it cannot be used to discover which
      // addresses are registered. Say what we actually know, not more.
      setResendState(response.ok ? 'sent' : 'failed');
    } catch (err) {
      setResendState('failed');
    }
  };

  const renderResend = () => (
    <>
      {resendState === 'sent' ? (
        <Alert variant="success" className="mt-3">
          If that address has an unverified account, a new link is on its way.
          It is valid for 24 hours.
        </Alert>
      ) : (
        <form onSubmit={handleResend} className="mt-3">
          <label htmlFor="resend-email" className="form-label">
            Send a new verification link
          </label>
          <div className="d-flex gap-2">
            <input
              id="resend-email"
              type="email"
              className="form-control"
              placeholder="you@example.com"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              required
            />
            <Button type="submit" variant="primary" disabled={resendState === 'sending'}>
              {resendState === 'sending' ? 'Sending…' : 'Send'}
            </Button>
          </div>
          {resendState === 'failed' && (
            <Alert variant="danger" className="mt-3">
              Could not send a new link right now. Please try again shortly.
            </Alert>
          )}
        </form>
      )}
    </>
  );

  return (
    <Container className="py-5" style={{ maxWidth: '560px' }}>
      <Card className="shadow-sm">
        <Card.Body className="p-4">
          <h1 className="h4 mb-3">Email verification</h1>

          {state === 'verifying' && (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
              <span>Verifying your email address…</span>
            </div>
          )}

          {state === 'success' && (
            <>
              <Alert variant="success">
                Your email address is verified. Thank you.
              </Alert>
              <Link to="/" className="btn btn-primary">Continue to dyner.z</Link>
            </>
          )}

          {state === 'expired' && (
            <>
              <Alert variant="warning">
                This link is no longer valid. Verification links expire after 24 hours
                and can only be used once.
              </Alert>
              {/* Explicit, because verification is not enforced: a stale link is a
                  minor inconvenience, not a lockout. */}
              <p className="text-muted mb-0">
                Your account still works normally in the meantime.
              </p>
              {renderResend()}
            </>
          )}

          {state === 'missing' && (
            <>
              <Alert variant="warning">
                This page needs a verification link from your email.
              </Alert>
              {renderResend()}
            </>
          )}

          {state === 'error' && (
            <>
              <Alert variant="danger">
                {message || 'Something went wrong verifying your email.'}
              </Alert>
              <p className="text-muted mb-0">
                Your account still works normally in the meantime.
              </p>
              {renderResend()}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default VerifyEmail;
